'use client'

import { useForm } from 'react-hook-form'

import { FilterPopoverButton } from '~/app/accounting/filters/FilterPopoverButton'
import {
  RangeInputs,
  useRangeFieldErrors,
} from '~/app/accounting/filters/RangeInputs'
import { usdRangeLabel } from '~/app/accounting/filters/helpers'

export type CostBasisFilterValue = { min: string; max: string }

export const EMPTY_COST_BASIS_FILTER: CostBasisFilterValue = {
  min: '',
  max: '',
}

const isEmpty = (value: CostBasisFilterValue) =>
  value.min === '' && value.max === ''

// Bounds on a lot's USD cost basis, so dust lots can be filtered out.
export const CostBasisFilterButton = ({
  value,
  onChange,
}: {
  value: CostBasisFilterValue
  onChange: (value: CostBasisFilterValue) => void
}) => {
  const form = useForm<CostBasisFilterValue>({ values: value })
  const { min, max } = form.watch()

  const { minError, maxError } = useRangeFieldErrors({
    min,
    max,
    setError: form.setError,
    clearErrors: form.clearErrors,
  })

  const hasChanged = min !== value.min || max !== value.max

  const activeLabel = usdRangeLabel(value)

  const clear = () => {
    form.reset(EMPTY_COST_BASIS_FILTER)
    onChange(EMPTY_COST_BASIS_FILTER)
  }

  return (
    <FilterPopoverButton
      label="Cost basis"
      activeLabel={activeLabel}
      isAppliedEmpty={isEmpty(value)}
      isLocalEmpty={isEmpty({ min, max })}
      isInvalid={!!minError || !!maxError}
      hasChanged={hasChanged}
      onApply={() => onChange({ min, max })}
      onReset={clear}
      onClear={clear}
      onClose={() => {
        if (hasChanged) form.reset(value)
      }}
    >
      {(focusAction) => (
        <RangeInputs
          control={form.control}
          minName="min"
          maxName="max"
          placeholder="$0"
          prefix="$"
          decimalScale={2}
          onComplete={focusAction}
        />
      )}
    </FilterPopoverButton>
  )
}
